import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  BookOpen,
  Plus,
  Search,
  MoreVertical,
  Edit2,
  Trash2,
  CheckCircle2,
  Eye,
  Star,
  Layers,
  DollarSign,
  Users,
  Clock,
  ExternalLink,
  Loader2,
  AlertTriangle,
  Image as ImageIcon,
  Sparkles,
  Globe,
  EyeOff,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Button from "../../components/common/Button";
import {
  CourseData,
} from "../../services/curriculumService";
import {
  useAdminCoursesQuery,
  useDeleteCourseMutation,
  useUpdateCourseMutation,
} from "../../hooks/queries/useCurriculumAdminQueries";
import { useToast } from "../../hooks/otherHooks/useToast";
import MediaPickerModal from "../../components/common/MediaPickerModal";
import { MediaAsset } from "../../services/mediaService";

const GlassCard = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={`cht-bg-white/80 cht-backdrop-blur-xl cht-border cht-border-gray-100 cht-shadow-[0_20px_50px_rgba(0,0,0,0.04)] ${className}`}
  >
    {children}
  </div>
);

const SuperAdminCourses: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();

  const { data: courses = [], isLoading: loading } = useAdminCoursesQuery();
  const deleteMutation = useDeleteCourseMutation();
  const updateMutation = useUpdateCourseMutation();

  const [searchQuery, setSearchQuery] = useState("");
  const [filterLevel, setFilterLevel] = useState("All");
  const [filterPayment, setFilterPayment] = useState("All");
  const [filterApproval, setFilterApproval] = useState("All");
  const [filterLive, setFilterLive] = useState("All");
  const [activeDropdown, setActiveDropdown] = useState<number | string | null>(
    null,
  );
  const [deletingId, setDeletingId] = useState<number | string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [mediaTargetCourse, setMediaTargetCourse] = useState<CourseData | null>(
    null,
  );
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click or tap
  useEffect(() => {
    const handlePointerDownOutside = (event: MouseEvent | TouchEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setActiveDropdown(null);
      }
    };
    if (activeDropdown !== null) {
      document.addEventListener("mousedown", handlePointerDownOutside);
      document.addEventListener("touchstart", handlePointerDownOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handlePointerDownOutside);
      document.removeEventListener("touchstart", handlePointerDownOutside);
    };
  }, [activeDropdown]);

  const handleSelectCourseMedia = async (asset: MediaAsset) => {
    if (!mediaTargetCourse?.id) return;
    const url =
      asset.url ||
      asset.thumbnail_url ||
      asset.external_url ||
      (typeof asset.file === "string" ? asset.file : "") ||
      "";
    try {
      await updateMutation.mutateAsync({
        id: mediaTargetCourse.id,
        data: { course_img_url: url },
      });
      addToast(
        `Updated cover image for "${mediaTargetCourse.title}" from Media Library!`,
        "success",
      );
    } catch (err) {
      console.error("Failed to update course image:", err);
      addToast("Failed to update course cover image.", "error");
    } finally {
      setMediaTargetCourse(null);
    }
  };

  const handleDeleteCourse = async (id: number | string) => {
    try {
      setIsDeleting(true);
      await deleteMutation.mutateAsync(id);
      addToast("Course removed successfully", "success");
      setDeletingId(null);
    } catch (err) {
      console.error("Failed to delete course:", err);
      addToast("Failed to delete course", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredCourses = useMemo(() => {
    return courses.filter((course) => {
      const instructorName =
        typeof course.instructor === "object" && course.instructor?.name
          ? course.instructor.name
          : course.instructor_name || "";
      const matchesSearch =
        course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        instructorName.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesLevel =
        filterLevel === "All" || course.level === filterLevel;
      const matchesPayment =
        filterPayment === "All" || course.payment_type === filterPayment;
      const matchesApproval =
        filterApproval === "All" ||
        (course.approval_status || "APPROVED") === filterApproval;
      const matchesLive =
        filterLive === "All" ||
        (filterLive === "Live" ? course.is_live === true : course.is_live !== true);
      return matchesSearch && matchesLevel && matchesPayment && matchesApproval && matchesLive;
    });
  }, [courses, searchQuery, filterLevel, filterPayment, filterApproval, filterLive]);

  const kpis = useMemo(() => {
    const total = courses.length;
    const paid = courses.filter(
      (c) => (c.payment_type || "Paid") === "Paid",
    ).length;
    const free = courses.filter((c) => c.payment_type === "Free").length;
    const pending = courses.filter(
      (c) => c.approval_status === "PENDING",
    ).length;
    const totalStudents = courses.reduce((acc, c) => {
      const count = parseInt(
        String(c.enrolled_count || "").replace(/[^0-9]/g, ""),
        10,
      );
      return acc + (isNaN(count) ? 0 : count);
    }, 0);
    return { total, paid, free, totalStudents, pending };
  }, [courses]);

  return (
    <div className="cht-space-y-8 cht-animate-in cht-fade-in cht-duration-700">
      <header className="cht-flex cht-flex-col md:cht-flex-row cht-items-start md:cht-items-center cht-justify-between cht-gap-4">
        <div>
          <div className="cht-flex cht-items-center cht-gap-2.5 cht-mb-1">
            <span className="cht-px-2.5 cht-py-0.5 cht-rounded-full cht-text-[10px] cht-font-black cht-uppercase cht-tracking-wider cht-bg-[#3e57da]/10 cht-text-[#3e57da]">
              SuperAdmin Console
            </span>
            <span className="cht-text-xs cht-text-gray-400 font-medium">
              Academic Governance
            </span>
          </div>
          <h1 className="cht-text-3xl cht-font-black cht-text-[#1e293b] cht-tracking-tight !cht-font-heading">
            Course Master Library
          </h1>
          <p className="cht-text-gray-500 cht-font-medium cht-text-sm">
            Full platform course creation, curriculum architecture, pricing, and
            instructional assignments.
          </p>
        </div>
        <Button
          variant="primary"
          leftIcon={<Plus size={18} />}
          className="cht-rounded-2xl cht-px-6 cht-shadow-md hover:cht-shadow-lg"
          onClick={() => navigate("/superadmin/dashboard/courses/create")}
        >
          Publish Platform Course
        </Button>
      </header>

      {/* KPI Grid */}
      <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 lg:cht-grid-cols-4 cht-gap-6">
        <GlassCard className="cht-p-6 cht-rounded-[28px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-4 cht-bg-blue-50 cht-text-[#3e57da] cht-rounded-2xl">
            <BookOpen size={24} />
          </div>
          <div>
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
              Total Catalog
            </p>
            <h4 className="cht-text-2xl cht-font-black cht-text-slate-900">
              {kpis.total}
            </h4>
          </div>
        </GlassCard>

        <GlassCard className="cht-p-6 cht-rounded-[28px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-4 cht-bg-emerald-50 cht-text-emerald-600 cht-rounded-2xl">
            <CheckCircle2 size={24} />
          </div>
          <div>
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
              Paid Tuitions
            </p>
            <h4 className="cht-text-2xl cht-font-black cht-text-slate-900">
              {kpis.paid}
            </h4>
          </div>
        </GlassCard>

        <GlassCard className="cht-p-6 cht-rounded-[28px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-4 cht-bg-indigo-50 cht-text-indigo-600 cht-rounded-2xl">
            <DollarSign size={24} />
          </div>
          <div>
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
              Free Access
            </p>
            <h4 className="cht-text-2xl cht-font-black cht-text-slate-900">
              {kpis.free}
            </h4>
          </div>
        </GlassCard>

        <GlassCard className="cht-p-6 cht-rounded-[28px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-4 cht-bg-amber-50 cht-text-amber-600 cht-rounded-2xl">
            <Users size={24} />
          </div>
          <div>
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
              Global Enrollments
            </p>
            <h4 className="cht-text-2xl cht-font-black cht-text-slate-900">
              {kpis.totalStudents > 0
                ? `${kpis.totalStudents.toLocaleString()}+`
                : "10k+"}
            </h4>
          </div>
        </GlassCard>
      </div>

      {/* Search & Filters Bar */}
      <div className="cht-flex cht-flex-col lg:cht-flex-row cht-items-center cht-gap-4 cht-bg-white cht-p-4 cht-rounded-[24px] cht-border cht-border-gray-100 cht-shadow-sm">
        <div className="cht-relative cht-flex-1 cht-w-full">
          <Search
            className="cht-absolute cht-left-4 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-400"
            size={18}
          />
          <input
            type="text"
            placeholder="Search courses by title, topic, or instructor..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="cht-w-full cht-bg-gray-50 cht-border-none cht-rounded-xl cht-py-3 cht-pl-12 cht-pr-4 cht-text-sm cht-font-semibold focus:cht-ring-2 focus:cht-ring-[#3e57da]/20 cht-transition-all"
          />
        </div>

        <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-2 cht-w-full lg:cht-w-auto">
          <div className="cht-flex cht-items-center cht-gap-1 cht-bg-gray-50 cht-p-1 cht-rounded-xl">
            {["All", "Beginner", "Intermidiate", "Advance"].map((level) => (
              <button
                key={level}
                onClick={() => setFilterLevel(level)}
                className={`cht-px-3 cht-py-1.5 cht-rounded-lg cht-text-[11px] cht-font-bold cht-transition-all ${
                  filterLevel === level
                    ? "cht-bg-[#1e293b] cht-text-white cht-shadow-xs"
                    : "cht-text-gray-500 hover:cht-text-gray-900"
                }`}
              >
                {level === "Intermidiate" ? "Intermediate" : level}
              </button>
            ))}
          </div>

          <div className="cht-flex cht-items-center cht-gap-1 cht-bg-gray-50 cht-p-1 cht-rounded-xl">
            {["All", "Paid", "Free"].map((type) => (
              <button
                key={type}
                onClick={() => setFilterPayment(type)}
                className={`cht-px-3 cht-py-1.5 cht-rounded-lg cht-text-[11px] cht-font-bold cht-transition-all ${
                  filterPayment === type
                    ? "cht-bg-[#3e57da] cht-text-white cht-shadow-xs"
                    : "cht-text-gray-500 hover:cht-text-gray-900"
                }`}
              >
                {type}
              </button>
            ))}
          </div>

          <div className="cht-flex cht-items-center cht-gap-1 cht-bg-gray-50 cht-p-1 cht-rounded-xl">
            {[
              { id: "All", label: "All Status" },
              { id: "APPROVED", label: "Approved" },
              {
                id: "PENDING",
                label: `Pending Review${kpis.pending > 0 ? ` (${kpis.pending})` : ""}`,
              },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setFilterApproval(tab.id)}
                className={`cht-px-3 cht-py-1.5 cht-rounded-lg cht-text-[11px] cht-font-bold cht-transition-all ${
                  filterApproval === tab.id
                    ? tab.id === "PENDING"
                      ? "cht-bg-amber-500 cht-text-white cht-shadow-xs"
                      : "cht-bg-emerald-600 cht-text-white cht-shadow-xs"
                    : "cht-text-gray-500 hover:cht-text-gray-900"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="cht-flex cht-items-center cht-gap-1 cht-bg-gray-50 cht-p-1 cht-rounded-xl">
            {[
              { id: "All", label: "All Visibility" },
              { id: "Live", label: "Live" },
              { id: "Draft", label: "Draft" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setFilterLive(tab.id)}
                className={`cht-px-3 cht-py-1.5 cht-rounded-lg cht-text-[11px] cht-font-bold cht-transition-all ${
                  filterLive === tab.id
                    ? tab.id === "Live"
                      ? "cht-bg-blue-600 cht-text-white cht-shadow-xs"
                      : "cht-bg-slate-700 cht-text-white cht-shadow-xs"
                    : "cht-text-gray-500 hover:cht-text-gray-900"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Courses Table */}
      <GlassCard className="cht-rounded-[32px] cht-overflow-hidden">
        {loading ? (
          <div className="cht-py-20 cht-flex cht-flex-col cht-items-center cht-justify-center cht-gap-3">
            <Loader2
              size={32}
              className="cht-animate-spin cht-text-[#3e57da]"
            />
            <p className="cht-text-xs cht-font-bold cht-text-slate-500">
              Loading course library...
            </p>
          </div>
        ) : filteredCourses.length === 0 ? (
          <div className="cht-py-20 cht-text-center cht-px-4">
            <BookOpen
              size={48}
              className="cht-text-slate-300 cht-mx-auto cht-mb-3"
            />
            <h4 className="cht-text-lg cht-font-bold cht-text-slate-800 !cht-font-heading">
              No courses found
            </h4>
            <p className="cht-text-xs cht-text-slate-500 cht-max-w-md cht-mx-auto cht-mt-1">
              {searchQuery
                ? `No courses matching "${searchQuery}". Try adjusting your filters.`
                : "Your platform catalog is ready for its first master course."}
            </p>
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Plus size={16} />}
              className="cht-mt-5 cht-rounded-xl"
              onClick={() => navigate("/superadmin/dashboard/courses/create")}
            >
              Create Course Now
            </Button>
          </div>
        ) : (
          <div className="cht-overflow-x-auto cht-min-h-[360px] cht-pb-24">
            <table className="cht-w-full">
              <thead>
                <tr className="cht-border-b cht-border-gray-100 cht-bg-slate-50/50">
                  <th className="cht-px-6 cht-py-4 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                    Course & Curriculum
                  </th>
                  <th className="cht-px-6 cht-py-4 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                    Instructor
                  </th>
                  <th className="cht-px-6 cht-py-4 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                    Tuition & Presence
                  </th>
                  <th className="cht-px-6 cht-py-4 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                    Status & Visibility
                  </th>
                  <th className="cht-px-6 cht-py-4 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                    Metrics & Social Proof
                  </th>
                  <th className="cht-px-6 cht-py-4 cht-text-right cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="cht-divide-y cht-divide-gray-50">
                {filteredCourses.map((course, index) => {
                  const instructorName =
                    typeof course.instructor === "object" &&
                    course.instructor?.name
                      ? course.instructor.name
                      : course.instructor_name || "Platform Lead";
                  const instructorPhoto =
                    typeof course.instructor === "object" &&
                    course.instructor?.get_photo_url
                      ? course.instructor.get_photo_url
                      : null;
                  const isMenuOpen = activeDropdown === course.id;
                  const isNearBottom =
                    index >= filteredCourses.length - 2 &&
                    filteredCourses.length > 2;

                  return (
                    <tr
                      key={course.id}
                      className={`hover:cht-bg-indigo-50/30 cht-transition-colors group ${
                        isMenuOpen ? "cht-relative cht-z-30" : ""
                      }`}
                    >
                      {/* Course Identity */}
                      <td className="cht-px-6 cht-py-4">
                        <div className="cht-flex cht-items-center cht-gap-3.5">
                          <div
                            onClick={() => setMediaTargetCourse(course)}
                            className="cht-w-14 cht-h-10 cht-rounded-xl cht-bg-slate-100 cht-overflow-hidden cht-relative cht-shrink-0 cht-border cht-border-gray-200 cht-cursor-pointer group/thumb"
                            title="Click to change cover image from Media Library"
                          >
                            {course.course_img_url ? (
                              <img
                                src={course.course_img_url}
                                alt={course.title}
                                className="cht-w-full cht-h-full cht-object-cover group-hover/thumb:cht-scale-105 cht-transition-transform"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src =
                                    "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800";
                                }}
                              />
                            ) : (
                              <div className="cht-w-full cht-h-full cht-flex cht-items-center cht-justify-center cht-text-[#3e57da]">
                                <BookOpen size={18} />
                              </div>
                            )}
                            <div className="cht-absolute cht-inset-0 cht-bg-black/50 cht-opacity-0 group-hover/thumb:cht-opacity-100 cht-transition-opacity cht-flex cht-items-center cht-justify-center cht-text-white">
                              <ImageIcon size={14} />
                            </div>
                          </div>
                          <div className="cht-min-w-0">
                            <h4 className="cht-text-sm cht-font-bold cht-text-slate-900 cht-truncate hover:cht-text-[#3e57da] cht-transition-colors">
                              {course.title}
                            </h4>
                            <div className="cht-flex cht-items-center cht-gap-2 cht-mt-0.5">
                              <span className="cht-text-[10px] cht-font-black cht-uppercase cht-px-2 cht-py-0.5 cht-rounded-md cht-bg-slate-100 cht-text-slate-600">
                                {course.level || "Beginner"}
                              </span>
                              <span className="cht-text-[11px] cht-text-slate-400">
                                {course.duration || "Self-paced"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Instructor */}
                      <td className="cht-px-6 cht-py-4">
                        <div className="cht-flex cht-items-center cht-gap-2.5">
                          {instructorPhoto ? (
                            <img
                              src={instructorPhoto}
                              alt={instructorName}
                              className="cht-w-7 cht-h-7 cht-rounded-full cht-object-cover cht-border cht-border-gray-200"
                            />
                          ) : (
                            <div className="cht-w-7 cht-h-7 cht-rounded-full cht-bg-[#3e57da]/10 cht-text-[#3e57da] cht-flex cht-items-center cht-justify-center cht-text-xs cht-font-bold">
                              {instructorName.charAt(0)}
                            </div>
                          )}
                          <p className="cht-text-xs cht-font-bold cht-text-slate-700 cht-truncate">
                            {instructorName}
                          </p>
                        </div>
                      </td>

                      {/* Tuition & Pricing */}
                      <td className="cht-px-6 cht-py-4">
                        <div>
                          <div className="cht-flex cht-items-center cht-gap-1.5">
                            <span
                              className={`cht-px-2 cht-py-0.5 cht-rounded-full cht-text-[9px] cht-font-black cht-uppercase ${
                                course.payment_type === "Free"
                                  ? "cht-bg-indigo-50 cht-text-indigo-600"
                                  : "cht-bg-emerald-50 cht-text-emerald-700"
                              }`}
                            >
                              {course.payment_type || "Paid"}
                            </span>
                            <span className="cht-text-xs cht-font-bold cht-text-slate-900">
                              ₦
                              {Number(
                                course.online_price || 0,
                              ).toLocaleString()}
                            </span>
                          </div>
                          {Number(course.onsite_price) > 0 && (
                            <p className="cht-text-[10px] cht-text-slate-400 cht-mt-0.5">
                              Onsite: ₦
                              {Number(course.onsite_price).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </td>

                      {/* Approval & Live Status */}
                      <td className="cht-px-6 cht-py-4">
                        <div className="cht-flex cht-flex-col cht-gap-1.5 cht-items-start">
                          {course.approval_status === "PENDING" ? (
                            <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-0.5 cht-rounded-full cht-text-[10px] cht-font-black cht-bg-amber-100 cht-text-amber-800 cht-border cht-border-amber-200">
                              <AlertTriangle
                                size={11}
                                className="cht-text-amber-600"
                              />
                              Pending Review
                            </span>
                          ) : (
                            <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-0.5 cht-rounded-full cht-text-[10px] cht-font-black cht-bg-emerald-100 cht-text-emerald-800 cht-border cht-border-emerald-200">
                              <CheckCircle2
                                size={11}
                                className="cht-text-emerald-600"
                              />
                              Approved
                            </span>
                          )}

                          {course.is_live ? (
                            <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-0.5 cht-rounded-full cht-text-[10px] cht-font-black cht-bg-blue-100 cht-text-blue-800 cht-border cht-border-blue-200">
                              <Globe size={11} className="cht-text-blue-600" />
                              Live to Public
                            </span>
                          ) : (
                            <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-0.5 cht-rounded-full cht-text-[10px] cht-font-bold cht-bg-slate-100 cht-text-slate-600 cht-border cht-border-slate-200">
                              <EyeOff size={11} className="cht-text-slate-500" />
                              Draft / Offline
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Social Proof Metrics */}
                      <td className="cht-px-6 cht-py-4">
                        <div className="cht-flex cht-items-center cht-gap-3">
                          <div className="cht-flex cht-items-center cht-gap-1 cht-text-amber-500">
                            <Star size={13} fill="currentColor" />
                            <span className="cht-text-xs cht-font-bold cht-text-slate-800">
                              {Number(course.rating) > 0 ? Number(course.rating).toFixed(1) : "0.0"}
                            </span>
                            <span className="cht-text-[10px] cht-text-slate-400">
                              ({course.review_count ?? 0})
                            </span>
                          </div>
                          <span className="cht-text-[11px] cht-text-slate-400">
                            {course.enrolled_count ?? 0} enrolled
                          </span>
                        </div>
                      </td>

                      {/* Actions */}
                      <td
                        className={`cht-px-6 cht-py-4 cht-text-right ${isMenuOpen ? "cht-relative cht-z-30" : ""}`}
                      >
                        <div className="cht-inline-flex cht-items-center cht-gap-1">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(
                                `/superadmin/dashboard/courses/${course.slug || course.id}/preview?courseId=${course.id}`,
                              );
                            }}
                            className="cht-w-8 cht-h-8 cht-flex cht-items-center cht-justify-center cht-rounded-xl cht-text-slate-400 hover:cht-bg-indigo-50 hover:cht-text-[#3e57da] cht-transition-colors cht-cursor-pointer"
                            title="View Course Material (Preview Outcome)"
                          >
                            <Eye size={16} />
                          </button>

                          <div
                            ref={isMenuOpen ? dropdownRef : undefined}
                            className="cht-relative cht-inline-block"
                          >
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveDropdown(isMenuOpen ? null : course.id!);
                              }}
                              className="cht-w-9 cht-h-9 cht-flex cht-items-center cht-justify-center cht-rounded-xl cht-text-slate-400 hover:cht-bg-slate-100 hover:cht-text-slate-800 cht-transition-colors cht-cursor-pointer"
                              title="Actions"
                            >
                              <MoreVertical size={18} />
                            </button>

                            <AnimatePresence>
                              {isMenuOpen && (
                                <motion.div
                                  initial={{
                                     opacity: 0,
                                     y: isNearBottom ? -6 : 6,
                                     scale: 0.96,
                                  }}
                                  animate={{ opacity: 1, y: 0, scale: 1 }}
                                  exit={{
                                     opacity: 0,
                                     y: isNearBottom ? -6 : 6,
                                     scale: 0.96,
                                  }}
                                  transition={{ duration: 0.15 }}
                                  className={`cht-absolute cht-right-0 ${
                                    isNearBottom
                                      ? "cht-bottom-full cht-mb-2"
                                      : "cht-top-full cht-mt-2"
                                  } cht-w-56 cht-bg-white cht-rounded-2xl cht-shadow-2xl cht-border cht-border-gray-100 cht-p-1.5 cht-z-50`}
                                >
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveDropdown(null);
                                      navigate(
                                        `/superadmin/dashboard/courses/${course.slug || course.id}/preview?courseId=${course.id}`,
                                      );
                                    }}
                                    className="cht-w-full cht-flex cht-items-center cht-gap-2.5 cht-px-3 cht-py-2.5 cht-text-xs cht-font-bold cht-text-slate-700 hover:cht-bg-indigo-50/70 hover:cht-text-[#3e57da] active:cht-bg-indigo-100/60 cht-rounded-xl cht-transition-colors text-left cht-cursor-pointer"
                                  >
                                    <Eye
                                      size={14}
                                      className="cht-text-[#3e57da]"
                                    />{" "}
                                    View Course Material
                                  </button>

                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveDropdown(null);
                                      navigate(
                                        `/superadmin/dashboard/courses/edit/${course.id}?type=course`,
                                      );
                                    }}
                                    className="cht-w-full cht-flex cht-items-center cht-gap-2.5 cht-px-3 cht-py-2.5 cht-text-xs cht-font-bold cht-text-slate-700 hover:cht-bg-indigo-50/70 hover:cht-text-[#3e57da] active:cht-bg-indigo-100/60 cht-rounded-xl cht-transition-colors text-left cht-cursor-pointer"
                                  >
                                    <Edit2
                                      size={14}
                                      className="cht-text-[#3e57da]"
                                    />{" "}
                                    Edit Course
                                  </button>

                                <button
                                  type="button"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    setActiveDropdown(null);
                                    try {
                                      await updateMutation.mutateAsync({
                                        id: course.id!,
                                        data: { is_live: !course.is_live } as any,
                                      });
                                      addToast(
                                        !course.is_live
                                          ? `"${course.title}" is now Live to the public!`
                                          : `"${course.title}" set to Draft (offline).`,
                                        "success"
                                      );
                                    } catch (err) {
                                      addToast("Failed to update course live status.", "error");
                                    }
                                  }}
                                  className="cht-w-full cht-flex cht-items-center cht-gap-2.5 cht-px-3 cht-py-2.5 cht-text-xs cht-font-bold cht-text-slate-700 hover:cht-bg-indigo-50/70 hover:cht-text-[#3e57da] active:cht-bg-indigo-100/60 cht-rounded-xl cht-transition-colors text-left cht-cursor-pointer"
                                >
                                  {course.is_live ? (
                                    <>
                                      <EyeOff size={14} className="cht-text-amber-500" />
                                      Set to Draft (Hide)
                                    </>
                                  ) : (
                                    <>
                                      <Globe size={14} className="cht-text-emerald-500" />
                                      Publish Live to Public
                                    </>
                                  )}
                                </button>

                                <div className="cht-h-px cht-bg-gray-100 cht-my-1" />

                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveDropdown(null);
                                    setDeletingId(course.id!);
                                  }}
                                  className="cht-w-full cht-flex cht-items-center cht-gap-2.5 cht-px-3 cht-py-2.5 cht-text-xs cht-font-bold cht-text-rose-600 hover:cht-bg-rose-50 active:cht-bg-rose-100/80 cht-rounded-xl cht-transition-colors text-left cht-cursor-pointer"
                                >
                                  <Trash2 size={14} /> Delete Course
                                </button>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </div>
                    </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>

      {/* Delete Confirmation Modal */}
      {deletingId && (
        <div className="cht-fixed cht-inset-0 cht-z-[10000] cht-flex cht-items-center cht-justify-center cht-p-4 cht-bg-slate-950/60 cht-backdrop-blur-sm">
          <div className="cht-bg-white cht-rounded-3xl cht-p-6 sm:cht-p-8 cht-max-w-md cht-w-full cht-shadow-2xl cht-border cht-border-gray-100 cht-space-y-4">
            <div className="cht-w-12 cht-h-12 cht-rounded-2xl cht-bg-rose-50 cht-text-rose-600 cht-flex cht-items-center cht-justify-center">
              <AlertTriangle size={24} />
            </div>
            <div>
              <h3 className="cht-text-lg cht-font-bold cht-text-slate-900 !cht-font-heading">
                Delete Course?
              </h3>
              <p className="cht-text-xs cht-text-slate-500 cht-mt-1 leading-relaxed">
                Are you sure you want to permanently remove this course and its
                associated syllabus? This action cannot be undone.
              </p>
            </div>
            <div className="cht-flex cht-items-center cht-justify-end cht-gap-3 cht-pt-2">
              <Button
                variant="ghost"
                size="sm"
                className="cht-rounded-xl"
                onClick={() => setDeletingId(null)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                className="cht-rounded-xl !cht-bg-rose-600 hover:!cht-bg-rose-700"
                onClick={() => handleDeleteCourse(deletingId)}
                isLoading={isDeleting}
              >
                Confirm Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Cover Image Media Library Picker Modal */}
      <MediaPickerModal
        isOpen={Boolean(mediaTargetCourse)}
        onClose={() => setMediaTargetCourse(null)}
        onSelect={handleSelectCourseMedia}
        allowedTypes={["image"]}
        title={`Select Cover Image for "${mediaTargetCourse?.title || "Course"}"`}
        selectedUrl={mediaTargetCourse?.course_img_url}
      />
    </div>
  );
};

export default SuperAdminCourses;
